import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    TextField,
    Button,
    IconButton,
    Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GT_COLORS } from '../../../../components/groups-teams/shared/constants';
import { PlacesTextField } from '../../../../components/groups-teams/shared/PlacesTextField';
import { GroupsTeamsService } from '../../../../services/groups-teams.service';
import { toast } from 'react-toastify';

interface CreateTeamModalProps {
    open: boolean;
    onClose: () => void;
    groupId: string;
    groupName: string;
    onCreated?: () => void;
}

export const CreateTeamModal: React.FC<CreateTeamModalProps> = ({
    open,
    onClose,
    groupId,
    groupName,
    onCreated,
}) => {
    const [name, setName] = useState('');
    const [location, setLocation] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSubmit = async () => {
        if (!name.trim()) return;
        setSaving(true);
        await GroupsTeamsService.createTeam(
            {
                name: name.trim(),
                location: location.trim() || undefined,
                groupId,
            },
            (isValid, message) => {
                setSaving(false);
                if (isValid) {
                    toast.success('Team created successfully.');
                    setName('');
                    setLocation('');
                    onClose();
                    onCreated?.();
                } else {
                    toast.error(message || 'Failed to create team.');
                }
            }
        );
    };

    const handleClose = () => {
        setName('');
        setLocation('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>Add Team to {groupName}</Typography>
                <IconButton onClick={handleClose} size="small" sx={{ color: GT_COLORS.textFaint }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px', mb: '4px' }}>
                        <Typography
                            sx={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: GT_COLORS.textMuted,
                                textTransform: 'uppercase',
                                letterSpacing: '.5px',
                            }}
                        >
                            Team Name
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: '#d32f2f', fontWeight: 500 }}>
                            *
                        </Typography>
                    </Box>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="e.g., Middlebury, Albany"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        autoFocus
                        sx={{ '& .MuiOutlinedInput-root': { fontSize: '14px', borderRadius: '8px' } }}
                    />
                </Box>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px', mb: '4px' }}>
                        <Typography
                            sx={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: GT_COLORS.textMuted,
                                textTransform: 'uppercase',
                                letterSpacing: '.5px',
                            }}
                        >
                            Location
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: GT_COLORS.textFaint, fontWeight: 400, fontStyle: 'italic' }}>
                            (optional)
                        </Typography>
                    </Box>
                    <PlacesTextField value={location} onChange={setLocation} />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                        color: GT_COLORS.textMuted,
                        borderColor: GT_COLORS.borderInput,
                        textTransform: 'none',
                        fontSize: '14px',
                        borderRadius: '8px',
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={!name.trim() || saving}
                    variant="contained"
                    sx={{
                        backgroundColor: GT_COLORS.primary,
                        textTransform: 'none',
                        fontSize: '14px',
                        borderRadius: '8px',
                        '&:hover': { backgroundColor: GT_COLORS.primaryHover },
                    }}
                >
                    Add Team
                </Button>
            </DialogActions>
        </Dialog>
    );
};
